import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { boxDrainCall, boxUndrainCall } from '../lib/box-lifecycle';
import { ago } from '../lib/time';
import { CopyAsCall } from './CopyAsCall';
import { Button } from './ui/Button';

// The details panel's drain row (#6563, worded by its v2 table): "Stop taking new work"
// (`POST /v1/runners/:id/drain`) or "Start taking new work" (`…/undrain`). Presenter: the
// panel owns the call, the confirm state and the API's refusal (`createBoxActions`).
//
// STOPPING IS NOT REMOVING, and the copy leads with the difference: the machine stays
// connected, its current task moves to another machine, and it takes nothing new until it
// is started again. Remove machine is its own action, below this one.
//
// CONFIRMED INLINE, not in a nested dialog: this sits inside the panel's dialog, and a
// second `role="alertdialog"` over it would fight it for the focus trap. The confirm uses
// the card menu's words (`fleet.machines.pause_*`), so both doors ask the same question.
//
// A STOP STICKS (`runners.drained_at`, #6455): the machine stays out, heartbeat or not,
// until it is started again. So a stopped machine shows when it stopped and the ONE way
// back, a single click, because all it does is let a checking-in machine take work again.

export function BoxDrainPanel(props: {
  /** The machine this row stops — the address both copied calls carry. */
  readonly boxId: string;
  /** Its name, which the confirm asks about. */
  readonly name: string;
  /** The drain mark off the utilization row, or null when it is taking work. */
  readonly drainedIso: string | null;
  readonly confirming: boolean;
  /** True while ANY of the panel's writes is in flight — a drain waits its turn. */
  readonly busy: boolean;
  readonly error: string | null;
  readonly onAsk: () => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly onUndrain: () => void;
}): JSX.Element {
  return (
    <div class="machine-panel__action-block">
      <div class="machine-panel__action">
        <Show
          when={props.drainedIso === null}
          fallback={
            <Button variant="outline" disabled={props.busy} onClick={props.onUndrain}>
              {t('fleet.machines.menu_resume')}
            </Button>
          }
        >
          <Button variant="outline" aria-expanded={props.confirming} onClick={props.onAsk}>
            {t('fleet.machines.menu_pause')}
          </Button>
        </Show>
        <Show
          when={props.drainedIso}
          fallback={<span class="machine-panel__action-desc">{t('fleet.panel.pause_desc')}</span>}
        >
          {(iso) => (
            <span class="machine-panel__action-desc">
              {t('fleet.panel.paused_since', { when: ago(iso()) })} {t('fleet.panel.resume_desc')}
            </span>
          )}
        </Show>
      </div>

      <Show when={props.confirming && props.drainedIso === null}>
        <div class="machine-panel__confirm">
          <p class="machine-panel__confirm-title">
            {t('fleet.machines.pause_title', { name: props.name })}
          </p>
          <p class="machine-panel__confirm-body">{t('fleet.machines.pause_body')}</p>
          <div class="machine-panel__confirm-actions">
            <Button variant="outline" disabled={props.busy} onClick={props.onConfirm}>
              {t('fleet.machines.pause_confirm')}
            </Button>
            <Button variant="ghost" onClick={props.onCancel}>
              {t('fleet.machines.pause_cancel')}
            </Button>
          </div>
        </div>
      </Show>

      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>

      <CopyAsCall
        calls={
          props.drainedIso === null
            ? [{ label: t('fleet.machines.menu_pause'), call: boxDrainCall(props.boxId) }]
            : [{ label: t('fleet.machines.menu_resume'), call: boxUndrainCall(props.boxId) }]
        }
      />
    </div>
  );
}
